import { limit, orderBy, where } from "firebase/firestore";
import moment from "moment";
import queryString from "query-string";
import { useEffect, useState } from "react";
import { Card, Col, Dropdown, Form, Row } from "react-bootstrap";
import { FaFilter, FaLink } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router";
import Select, { createFilter } from "react-select";

import {
    useAppCollection,
    useOrganizationCollection,
    useResultCollectionGroup,
} from "../../api/firestoreWrappers.generated.jsx";
import Layout, { Title } from "../../components/Layout/Layout.jsx";
import LoadMoreButton from "../../components/LoadMoreButton/index.jsx";
import { DateFilter } from "../../components/MissionFilters/index.jsx";
import ResultsList, {
    hasPublishedFiles,
} from "../../components/ResultsList/index.jsx";
import { useUser } from "../../contexts/user_provider.jsx";
import urls from "../../urls.jsx";

export default function AdminResultListPage() {
    const resultsPerPage = 50;

    const location = useLocation();
    const queryParams = queryString.parse(location.search);
    const navigate = useNavigate();
    const { getUserIsAdmin } = useUser();
    const isAdmin = getUserIsAdmin();

    const [organizationMap] = useOrganizationCollection(
        [orderBy("name", "asc")],
        { type: "map" },
    );
    const organizationOptions = Object.values(organizationMap)
        .filter((org) => !org.deleted)
        .map((org) => ({
            value: org.id,
            label: org.name,
        }));

    const [allApps] = useAppCollection([orderBy("name", "asc")]);
    const nameCounts = allApps.reduce((counts, app) => {
        counts[app.name] = (counts[app.name] || 0) + 1;
        return counts;
    }, {});
    const appOptions = allApps.map((app) => ({
        value: app.id,
        label:
            nameCounts[app.name] > 1 && app?.provider?.name
                ? `${app.name} (${app.provider.name})`
                : app.name,
    }));

    const [filters, setFilters] = useState({
        startDate: queryParams.startDate
            ? new Date(Number(queryParams.startDate))
            : undefined,

        endDate: queryParams.endDate
            ? new Date(Number(queryParams.endDate))
            : undefined,

        hidePublished: queryParams.hidePublished === "true",
        organizationId: queryParams.organizationId,
        appId: queryParams.appId,
    });

    const [resultsToLoad, setResultsToLoad] = useState(resultsPerPage);
    const [results, isLoading] = useResultCollectionGroup([
        where("hasFiles", "==", true),
        ...(filters?.organizationId
            ? [where("organizationId", "==", filters.organizationId)]
            : []),
        ...(filters?.appId ? [where("app.id", "==", filters.appId)] : []),
        ...(filters?.startDate
            ? [where("timestamp", ">=", filters?.startDate)]
            : []),
        ...(filters?.endDate
            ? [where("timestamp", "<=", filters?.endDate)]
            : []),
        orderBy("timestamp", "desc"),
        limit(resultsToLoad),
    ]);

    useEffect(() => {
        const newQuery = {
            startDate: filters.startDate
                ? filters.startDate.getTime()
                : undefined,
            endDate: filters.endDate ? filters.endDate.getTime() : undefined,
            hidePublished: filters.hidePublished ? "true" : undefined,
            organizationId: filters.organizationId,
            appId: filters.appId,
        };

        // Remove undefined keys to clean up the URL
        const cleanedQuery = Object.fromEntries(
            Object.entries(newQuery).filter(([, v]) => v !== undefined),
        );

        const newSearch = queryString.stringify(cleanedQuery);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
    }, [filters, location.pathname, navigate]);

    const seenHashes = new Set();

    const resultsByDateAndApp = results.reduce((acc, result) => {
        const originalFiles = result.files ?? [];

        // Deduplicate by hash (latest wins)
        const dedupedFiles = originalFiles.filter((file) => {
            if (!file.hash) return false;

            if (seenHashes.has(file.hash)) {
                return false;
            }

            seenHashes.add(file.hash);
            return true;
        });

        const files = filters.hidePublished
            ? dedupedFiles.filter((file) => !file.published)
            : dedupedFiles;

        // Skip results with no files
        if (files.length === 0) {
            return acc;
        }

        // Skip results without published files
        if (!hasPublishedFiles(files, isAdmin)) {
            return acc;
        }

        const date = moment(result.timestamp.toDate()).format("YYYY-MM-DD");
        const appId = result.app.id;

        acc[date] ??= {};
        acc[date][appId] ??= [];
        acc[date][appId].push({
            ...result,
            files,
        });

        return acc;
    }, {});

    return (
        <Layout>
            <title>All Results | MyCorvus</title>
            <Card className="mb-3">
                <Title title={"All Results"} />
            </Card>
            <Row className="mb-2">
                <Col xs={12} md={4}>
                    <Form.Check
                        type="switch"
                        id="hide-published-switch"
                        label="Hide published results"
                        checked={filters.hidePublished ?? false}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                hidePublished: e.target.checked,
                            }))
                        }
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={4}>
                    <DateFilter
                        className={"mb-2"}
                        filters={filters}
                        setFilters={setFilters}
                    />
                </Col>
                <Col xs={12} md={4}>
                    <Select
                        className={"mb-2"}
                        isClearable
                        value={
                            organizationOptions.find(
                                (o) => o.value === filters.organizationId,
                            ) ?? null
                        }
                        onChange={(option) => {
                            setFilters((prev) => {
                                // cleared
                                if (!option) {
                                    const rest = { ...prev };
                                    delete rest.organizationId;
                                    return rest;
                                }

                                // selected
                                return {
                                    ...prev,
                                    organizationId: option.value,
                                };
                            });
                        }}
                        options={organizationOptions}
                        placeholder="Filter by organization"
                        filterOption={createFilter({
                            stringify: (option) => option.label,
                        })} // Only match labels when typing
                        styles={{
                            menu: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                    />
                </Col>
                <Col xs={12} md={4}>
                    <Select
                        className={"mb-2"}
                        isClearable
                        value={
                            appOptions.find((o) => o.value === filters.appId) ??
                            null
                        }
                        onChange={(option) => {
                            setFilters((prev) => {
                                // cleared
                                if (!option) {
                                    const rest = { ...prev };
                                    delete rest.appId;
                                    return rest;
                                }

                                // selected
                                return {
                                    ...prev,
                                    appId: option.value,
                                };
                            });
                        }}
                        options={appOptions}
                        placeholder="Filter by app"
                        filterOption={createFilter({
                            stringify: (option) => option.label,
                        })} // Only match labels when typing
                        styles={{
                            menu: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                    />
                </Col>
            </Row>

            <ResultsList
                resultsByDateAndApp={resultsByDateAndApp}
                isAdmin={isAdmin}
                showOrganizationName={true}
                organizationMap={organizationMap}
                extraDropdownItems={({ result }) => (
                    <>
                        <Dropdown.Item
                            onClick={() =>
                                navigate(
                                    urls.locationResults(
                                        result.organizationId,
                                        result.locationId,
                                    ),
                                )
                            }
                        >
                            <FaLink className="me-2" />
                            Go to Loc Results
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() =>
                                setFilters((prev) => {
                                    return {
                                        ...prev,
                                        organizationId: result.organizationId,
                                    };
                                })
                            }
                        >
                            <FaFilter className="me-2" />
                            Filter organization
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() =>
                                setFilters((prev) => {
                                    return {
                                        ...prev,
                                        appId: result.app.id,
                                    };
                                })
                            }
                        >
                            <FaFilter className="me-2" />
                            Filter app
                        </Dropdown.Item>
                    </>
                )}
            />

            <LoadMoreButton
                disabled={resultsToLoad > results.length}
                isLoading={isLoading}
                setIsLoading={() => {}}
                load={() => {
                    setResultsToLoad(resultsToLoad + resultsPerPage);
                }}
                showLoadAll={false}
                loadAll={() => {}}
            />
        </Layout>
    );
}
